/* eslint-disable max-lines-per-function */
import {HIDDEN_FACETS} from '../data/facets-store';

/**
 * Slugify a string
 * @param {String} text
 * @returns {void}
 */
const slugify = (text) => {
	return text.toLowerCase().replace(/[^\w ,-]+/g, '').replace(/ +/g, '-');
};

const createBannerElement = (banner) => {
	return {
		content: banner.value,
		isInlineBanner: true,
		uid: 'ssib-' + banner.config.position.index
	};
};

const isDeptCategory = (category) => {
	const regex = /__dept__/;

	// If not a department category return
	if (!category.match(regex)) {
		return false;
	}

	// Return department code
	const start = category.search(regex) + 8;

	return category.slice(start, category.length);
};

const removeHiddenFacets = (facets) => {
	const facetsToHide = HIDDEN_FACETS;
	return facets.filter((item) => {
		return !facetsToHide.includes(item.field) || (item.field === 'team' || item.field === 'league') && item.values.length === 1;
	});
};

export const sortDepartments = () => draftSearchData => {
	const departments = draftSearchData.facets.find(facet => facet.field === 'facet_department');

	/*
	 * If there's no department facet or we're in a
	 * department category already we don't need to sort
	 */
	if (typeof departments === 'undefined' || isDeptCategory(window.mivaJS.Category_Code)) {
		return draftSearchData;
	}

	return draftSearchData;
};

export const removeDepartmentFacets = (map) => draftSearchData => {
	const departments = draftSearchData.facets.find(facet => facet.field === 'facet_department');

	/*
	 * If there's no department facet or we're in a
	 * department category already we don't need to filter
	 */
	if (typeof departments === 'undefined' || isDeptCategory(window.mivaJS.Category_Code)) {
		return draftSearchData;
	}

	const options = departments.values;
	const active = options.filter((item) => item.active);
	let facetsToHide = [];
	const deptSortOrder = typeof window.mivaJS.DeptSortOrder === 'undefined' ? false : window.mivaJS.DeptSortOrder;
	let topDepartments = [];

	/*
	 * Check which departments are active and
	 * check the map for any facets we need to hide
	 */
	options.forEach(function (item) {
		if (typeof map[item.label] !== 'undefined' && !item.active) {
			facetsToHide = facetsToHide.concat(map[item.label]);
		}
	});

	// Sort department options
	if (deptSortOrder) {
		deptSortOrder.forEach(function (dept) {
			const index = options.findIndex(p => slugify(p.label) === dept);
			if (index !== -1) {
				topDepartments.push.apply(topDepartments, options.splice(index, 1));
			}
		});
		options.unshift.apply(options, topDepartments);
	}

	/*
	 * If there are active department facets, hide them
	 * otherwise, hide the "default" set of facets
	 */
	if (active.length) {
		draftSearchData.facets = draftSearchData.facets.filter((item) => {
			let result = !facetsToHide.includes(item.field);
			return result;
		});
	} else {
		draftSearchData.facets = removeHiddenFacets(draftSearchData.facets);
	}

	return draftSearchData;
};

export const removeCategoryFacets = (map) => draftSearchData => {
	const categories = window.mivaJS.returnsCategories ? window.mivaJS.returnsCategories : {};
	let currCategory = window.mivaJS.Category_Code;
	let facets = [];
	if (typeof currCategory === 'undefined' || !categories.length) {
		return draftSearchData;
	}

	categories.some(function (o) {
		facets = map[o.label.toLowerCase()];
		if (facets) {
			return o;
		}
	});

	if (facets && facets.length) {
		draftSearchData.facets = draftSearchData.facets.filter((item) => !facets.includes(item.field));
	}

	return draftSearchData;
};

export const updateResults = () => async draftSearchData => {

	let updatedResults = {};
	const resultsToUpdate = [];
	const pushedImpressions = [];
	let impressionArray = [];

	for (let result of draftSearchData.results) {
		if (!updatedResults[result.uid]) {
			resultsToUpdate.push(result.uid);
		}
	}

	if (resultsToUpdate.length) {
		// Update inventory and price
		const response = await fetch(
			window.mivaJS.Japi_Url,
			{
				body: JSON.stringify({
					function: 'load-runtime-products-by-id',
					// eslint-disable-next-line camelcase
					product_ids: resultsToUpdate
				}),
				headers: {
					'Content-Type': 'application/json'
				},
				method: 'POST'
			}
		);

		// Handle the response as JSON
		const jsonResponse = await response.json();

		// Check the response status
		if (jsonResponse.success) {

			// Create a unique dict of update data
			for (let updatedResult of jsonResponse.data) {

				updatedResults[updatedResult.id] = {
					// eslint-disable-next-line camelcase
					base_price: updatedResult.base_price,
					// eslint-disable-next-line camelcase
					inventory_active: updatedResult.inv_active,
					// eslint-disable-next-line camelcase
					inventory_count: updatedResult.inv_available,
					// eslint-disable-next-line camelcase
					inventory_level: updatedResult.inv_level,
					price: updatedResult.price,
					retail: updatedResult.retail
				};
			}
		}
	}

	// Mesh update data into results data
	let positionCounter = 0;
	draftSearchData.results = draftSearchData.results.map(result => {
		positionCounter++;
		const updatedResult = {
			...result,
			...updatedResults[result.uid]
		};

		// START: Push to GTM
		if (!pushedImpressions.includes(updatedResult.uid)) {
			pushedImpressions.push(updatedResult.uid);
			impressionArray.push({
				brand: updatedResult.featured_brand ? updatedResult.featured_brand : updatedResult.brand,
				category: updatedResult.facet_department ? updatedResult.facet_department : updatedResult.facet_product_type,
				id: updatedResult.ss_sku,
				list: window.mivaJS.Page_Code === 'CTGY' ? 'Category Results' : 'Search Results',
				name: updatedResult.name,
				position: positionCounter,
				price: updatedResult.price,
				variant: ''
			});
		}
		// END: Push to GTM

		return updatedResult;

	});

	if (typeof dataLayer !== 'undefined') {
		window.dataLayer.push({
			ecommerce: {
				currencyCode: 'USD',
				impressions: impressionArray
			},
			event: 'impressionsPushed'
		});
	}

	return draftSearchData;
};

export const insertInlineBanners = () => draftSearchData => {
	const merchandisingContent = draftSearchData?.merchandising?.content ? draftSearchData.merchandising.content : {};
	const inlineBanners = merchandisingContent.inline ? Array.from(merchandisingContent.inline) : [];

	if (!inlineBanners.length) {
		return draftSearchData;
	}

	const tailBanners = [];
	const pageBegin = draftSearchData.pagination.begin - 1;
	const pageEnd = draftSearchData.pagination.end;

	inlineBanners
		.filter((banner) => {
			const bannerElement = createBannerElement(banner);
			const bannerPosition = banner.config.position.index;

			if (bannerPosition >= pageBegin) {
				if (bannerPosition <= pageEnd - 1) {
					return true;
				}
				tailBanners.push(bannerElement);
			}

			return false;
		})
		.forEach((banner) => {
			const bannerElement = createBannerElement(banner);
			draftSearchData.results.splice(banner.config.position.index - pageBegin, 0, bannerElement);
		});

	return draftSearchData;
};

export const insertFacetOptionOverflows = () => draftSearchData => {
	const facets = draftSearchData?.facets ? Array.from(draftSearchData.facets) : [];

	if (!facets.length) {
		return draftSearchData;
	}

	facets.forEach((facet, index) => {
		if (facet.overflow) {
			return;
		}

		const remainingFacetValues = facet.values.length > 7 ? facet.values.length - 7 : '';
		const defaultOverflow = {
			$active: false,
			limit: 7,
			remaining: remainingFacetValues
		};

		draftSearchData.facets[index].overflow = defaultOverflow;
	});

	return draftSearchData;
};
